import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import {
  formatDateRange,
  getDurationString,
  localDateTimeInputToApi,
  toLocalDateTimeInput,
} from 'us-react-components/dist/utils/date';
import { Booking, BookingService, SitterService } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

export const BookingReschedule = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores, services: { tracker } } = useDependencies();
  const user = stores.user.user;

  const [booking, setBooking] = useState<Booking | null>(null);
  const [startInput, setStartInput] = useState<string>('');
  const [endInput, setEndInput] = useState<string>('');
  const [unavailable, setUnavailable] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [dateError, setDateError] = useState<string>('');
  const [availabilityWarning, setAvailabilityWarning] = useState<string>('');
  const [bypassAvailability, setBypassAvailability] = useState<boolean>(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Reschedule';
  }, []);

  useEffect(() => {
    const bookingId = Number(pathId);
    if (!bookingId || !user?.id) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await BookingService.get(bookingId, { params: { userId: user.id } });
        const b = (resp.data?.data ?? null) as Booking | null;
        setBooking(b);
        if (b) {
          setStartInput(toLocalDateTimeInput(b.start));
          setEndInput(toLocalDateTimeInput(b.end));
          tracker.track('booking-reschedule', { action: 'reschedule-flow-started' });
        }
      } catch (err) {
        console.warn('[BookingReschedule] load failed', err);
      }
    };
    void load();
  }, [pathId, user?.id, tracker]);

  const isViewerSitter = !!(user && !user.parent);
  const profile = isViewerSitter ? booking?.parent : booking?.sitter;
  const heading = booking
    ? t('pages.bookingReschedule.heading', {
        type: booking.type ?? '',
        name: profile?.firstName ?? '',
      })
    : '';

  const subtitle = booking
    ? `${t('pages.bookingReschedule.currentLabel')}: ${formatDateRange(booking.start, booking.end)}`
    : '';

  const checkDates = (startVal: string, endVal: string): void => {
    const startIso = localDateTimeInputToApi(startVal);
    const endIso = localDateTimeInputToApi(endVal);
    if (!startIso || !endIso) {
      setDateError('');
      return;
    }
    if (new Date(endIso) <= new Date(startIso)) {
      setDateError(t('pages.bookingReschedule.endAfterStart'));
    } else {
      setDateError('');
    }
  };

  const postReschedule = async (startIso: string, endIso: string): Promise<void> => {
    if (!booking?.id || !user?.id) return;
    setSubmitting(true);
    try {
      await BookingService.patch(
        booking.id,
        {
          id: booking.id,
          start: startIso,
          end: endIso,
          status: {
            code: isViewerSitter ? 'sitter_reschedule' : 'parent_reschedule',
          },
        } as unknown as Booking,
        { params: { userId: user.id } },
      );
      tracker.track('booking-reschedule', { action: 'reschedule-request-sent' });
      navigate(`/bookings/${booking.id}`);
    } catch (err: unknown) {
      setSubmitting(false);
      const anyErr = err as { response?: { data?: { errors?: Array<{ message?: string }> } } };
      const msgs = (anyErr?.response?.data?.errors ?? []).map((e) => e.message ?? '').filter(Boolean);
      if (msgs.length) {
        toast.error(msgs.join(' · '));
      } else {
        toast.error(t('pages.bookingReschedule.submitFailed'));
      }
      setUnavailable(true);
    }
  };

  const sendRequest = async (force = false): Promise<void> => {
    if (!booking?.id || !user?.id || submitting) return;
    const startIso = localDateTimeInputToApi(startInput);
    const endIso = localDateTimeInputToApi(endInput);
    if (!startIso || !endIso) return;
    if (booking.start === startIso && booking.end === endIso) {
      setDateError(t('pages.bookingReschedule.notEnteredNewTime'));
      return;
    }

    // Parent-initiated flow: pre-flight availability check (mirrors Angular reschedule.js:104-148).
    const sitterId = booking.sitter?.id;
    const canCheck = !isViewerSitter && !!sitterId && !force && !bypassAvailability;
    if (canCheck) {
      try {
        await SitterService.getSchedules({
          params: {
            available: true,
            bookingId: booking.id,
            sitterId,
            start: startIso,
            end: endIso,
          },
        });
        setAvailabilityWarning('');
      } catch {
        const firstName = booking.sitter?.firstName ?? t('pages.bookingReschedule.theSitter');
        setAvailabilityWarning(
          t('pages.bookingReschedule.unavailableBanner', { name: firstName }),
        );
        return;
      }
    }

    setAvailabilityWarning('');
    await postReschedule(startIso, endIso);
  };

  const sendAnyway = async (): Promise<void> => {
    setBypassAvailability(true);
    await sendRequest(true);
  };

  const buttonLabel = unavailable
    ? t('pages.bookingReschedule.changeDateTime')
    : t('pages.bookingReschedule.sendRequest');

  if (!booking) {
    return (
      <div className='container py-8'>
        <h2 className='text-base'>
          <i className='fa-solid fa-spinner fa-spin mr-2' />
          {t('pages.bookingReschedule.loading')}
        </h2>
      </div>
    );
  }

  return (
    <div className='container py-8'>
      <div className='us-card rounded p-4'>
        <div className='flex items-start gap-3 mb-4'>
          {profile?.pictureUrl && (
            <img
              src={profile.pictureUrl}
              alt={profile.name ?? ''}
              className='w-20 h-20 rounded object-cover flex-shrink-0'
            />
          )}
          <div>
            <h1 className='text-xl font-semibold mb-1'>{heading}</h1>
            {subtitle && <div className='text-sm'>{subtitle}</div>}
          </div>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4'>
          <div>
            <label htmlFor='reschedule-start' className='block text-sm mb-1 font-semibold'>
              {t('pages.bookingReschedule.startLabel')}
            </label>
            <input
              id='reschedule-start'
              type='datetime-local'
              value={startInput}
              onChange={(e): void => {
                setStartInput(e.target.value);
                checkDates(e.target.value, endInput);
              }}
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            />
          </div>
          <div>
            <label htmlFor='reschedule-end' className='block text-sm mb-1 font-semibold'>
              {t('pages.bookingReschedule.endLabel')}
            </label>
            <input
              id='reschedule-end'
              type='datetime-local'
              value={endInput}
              onChange={(e): void => {
                setEndInput(e.target.value);
                checkDates(startInput, e.target.value);
              }}
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            />
          </div>
        </div>

        {dateError && (
          <div className='text-red-600 text-sm mb-3'>{dateError}</div>
        )}

        {getDurationString(startInput, endInput) && (
          <div className='mb-4 text-sm'>
            <span className='font-semibold'>Duration:</span> {getDurationString(startInput, endInput)}
          </div>
        )}

        {availabilityWarning && (
          <div
            id='availability-warning'
            className='mb-4 p-3 border border-amber-300 bg-amber-50 rounded text-sm'
            role='alert'
          >
            <div className='mb-2'>{availabilityWarning}</div>
            <Button
              id='btn-send-anyway'
              buttonType={ButtonType.secondary}
              onClick={sendAnyway}
              disabled={submitting}
            >
              Send anyway
            </Button>
          </div>
        )}

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
          <Button
            id='btn-check-availability'
            buttonType={ButtonType.primary}
            onClick={(): Promise<void> => sendRequest()}
            disabled={submitting || !!dateError}
            className='w-full'
          >
            {buttonLabel}
          </Button>
          {unavailable && (
            <Button
              id='btn-see-available-sitters'
              buttonType={ButtonType.secondary}
              onClick={(): void => navigate('/sitters')}
              className='w-full'
            >
              {t('pages.bookingReschedule.seeAvailable')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});
